import type { LevelStreamer } from "../level/LevelStreamer";
import type { Player } from "../player/Player";
import type { WorldRotator } from "../world/WorldRotator";
import type { CameraBank } from "./CameraBank";
import type { MirrorTrickFx } from "./MirrorTrickFx";

const REVERSE_GATE_ID = "reverse_gate";
const FLIP_ROLL_RAD = Math.PI;

// ReverseGateFx ties together every system that needs to know "world
// is upside-down right now" for the duration of the Reverse Gate
// boss. The boss phrase is authored in src/game/level/bosses.ts; the
// LevelStreamer fires onBossStart with the marker.id when that
// phrase is attached.
//
// On boss start:
//   - CameraBank.setSustainedRoll(PI) -> camera rolls 180° on Z
//     (CameraBank's own lerp gives the roll a ~0.4s ease-in)
//   - WorldRotator.setGravityFlipped(true) -> physics gravity to +Y
//   - WorldRotator.setFloorOffset(...) -> the panel directly opposite
//     the natural floor becomes the highlighted/checked tile
//   - Player.setGravityFlipped(true) -> mirrors the soft Y-pin sign
//     so the ball still pins at the rest position under +Y gravity
//   - MirrorTrickFx.setForcedInvert(true) -> rotate inputs feel
//     visually correct from the player's flipped POV (left-on-screen
//     still points the world the way they expect)
//
// On boss cleared (or any state-transition clear()), all five flips
// are restored to their defaults.
export class ReverseGateFx {
  private streamer: LevelStreamer;
  private rotator: WorldRotator;
  private player: Player;
  private cameraBank: CameraBank;
  private mirror: MirrorTrickFx;
  private active = false;
  private startUnsub: (() => void) | null = null;
  private endUnsub: (() => void) | null = null;

  constructor(
    streamer: LevelStreamer,
    rotator: WorldRotator,
    player: Player,
    cameraBank: CameraBank,
    mirror: MirrorTrickFx
  ) {
    this.streamer = streamer;
    this.rotator = rotator;
    this.player = player;
    this.cameraBank = cameraBank;
    this.mirror = mirror;

    this.startUnsub = this.streamer.onBossStart.on((e) => {
      if (e.marker.id !== REVERSE_GATE_ID) return;
      this.engage();
    });
    this.endUnsub = this.streamer.onBossCleared.on((e) => {
      if (e.marker.id !== REVERSE_GATE_ID) return;
      this.disengage();
    });
  }

  // Restore everything to defaults. MorphHexGame calls this on every
  // state transition that recycles the streamer (showMenu, startGame,
  // advanceLevel, onDeath) so a leftover Reverse Gate state from a
  // previous run never bleeds into the next one.
  clear(): void {
    if (!this.active) return;
    this.disengage();
  }

  // True while the camera is inverted. MorphHexGame uses this to pause
  // BankingFx for the duration of the boss so the per-segment bank
  // values don't fight with the sustained 180° flip.
  isActive(): boolean {
    return this.active;
  }

  dispose(): void {
    this.startUnsub?.();
    this.endUnsub?.();
    this.startUnsub = null;
    this.endUnsub = null;
    this.clear();
  }

  private engage(): void {
    if (this.active) return;
    this.active = true;
    this.cameraBank.setSustainedRoll(FLIP_ROLL_RAD);
    this.rotator.setGravityFlipped(true);
    // Opposite-panel remap. Only valid for even-sided polygons; the
    // authored phrase pins to hexagons, so this is safe. For odd-sided
    // shapes (just in case the phrase ever gets edited) we round the
    // half-shift, which still highlights a panel near the new floor.
    this.rotator.setFloorOffset((idx, sides) => {
      const half = Math.round(sides / 2);
      return (idx + half) % sides;
    });
    this.player.setGravityFlipped(true);
    this.mirror.setForcedInvert(true);
  }

  private disengage(): void {
    if (!this.active) return;
    this.active = false;
    this.cameraBank.setSustainedRoll(0);
    this.rotator.setGravityFlipped(false);
    this.rotator.setFloorOffset(null);
    this.player.setGravityFlipped(false);
    this.mirror.setForcedInvert(null);
  }
}
